import { useState, useEffect, useRef, useCallback } from 'react';

interface UseInViewportOptions {
  threshold?: number;
  rootMargin?: string;
}

interface UseInViewportReturn {
  isInViewport: boolean;
  elementRef: React.RefObject<HTMLDivElement | null>;
}

const useInViewport = (options: UseInViewportOptions = {}): UseInViewportReturn => {
  const { threshold = 0.1, rootMargin = '0px' } = options;
  const [isInViewport, setIsInViewport] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    setIsInViewport(entry.isIntersecting);
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Check if IntersectionObserver is supported
    if (!window.IntersectionObserver) {
      // Fallback: Assume element is always in viewport
      setIsInViewport(true);
      return;
    }

    const observer = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersection, threshold, rootMargin]);

  return {
    isInViewport,
    elementRef
  };
};

export default useInViewport;
